import { computed, nextTick, ref } from 'vue';
import { cotizaTodas } from '@/services/api';
import { isChubbLegacyEnabled } from '@/config/chubbLegacy';
import { buildLegacyChubbPayload, isChubbPlanPaymentAllowed } from '@/services/chubbLegacyQuote';
import { maintainExpandMode } from './expandMode';
import { mapFormaPago, mapPaquete } from './paymentMaps';
import { resolveIdCotizacion } from './quoteId';
import type {
  PlanSelection,
  QuoteGeneration,
  QuoteSources,
  ResultsViewEmit,
  SelectedCompanyRef,
} from './types';

interface RecotizarDeps {
  quoteGeneration: QuoteGeneration;
  sources: QuoteSources;
  plan: PlanSelection;
  selectedCompany: SelectedCompanyRef;
  emit: ResultsViewEmit;
}

interface QuoteRequest {
  idCotizacion: number;
  formaPago: string;
  paquete: string;
}

async function requoteQualitas({ quoteGeneration, emit }: RecotizarDeps, req: QuoteRequest): Promise<void> {
  try {
    await quoteGeneration.fetchQuotes({ ...req, showGlobalLoading: false, company: 'QUALITAS' });
    if (quoteGeneration.quotesResponse.value) emit('quotesUpdated', quoteGeneration.quotesResponse.value);
  } catch (qualitasError) {
    console.error('❌ Error al obtener cotización de Qualitas:', qualitasError);
    // The previous quote belongs to another plan or payment method; showing it would be a wrong price.
    quoteGeneration.quotesResponse.value = null;
    const current = quoteGeneration.error.value;
    if (current && !current.includes('Qualitas') && !current.includes('ANA')) {
      quoteGeneration.error.value = `Qualitas: ${current}`;
    }
  }
}

async function requoteAna({ sources }: RecotizarDeps, req: QuoteRequest): Promise<void> {
  try {
    sources.anaQuotes.value = await cotizaTodas({ ...req, company: 'ANA' });
  } catch (anaError) {
    console.error('❌ Error al obtener cotización de ANA Seguros:', anaError);
    sources.anaQuotes.value = null;
  }
}

async function requoteChubb(deps: RecotizarDeps, req: QuoteRequest): Promise<void> {
  const { quoteGeneration, sources, selectedCompany } = deps;
  if (!isChubbLegacyEnabled()) {
    quoteGeneration.clearChubbQuote();
    return;
  }
  if (!isChubbPlanPaymentAllowed(req.formaPago, req.paquete)) {
    quoteGeneration.clearChubbQuote();
    if (selectedCompany.value === 'CHUBB') {
      selectedCompany.value = sources.quotesResponse.value?.Cobertura ? 'QUALITAS' : 'ANA';
    }
    return;
  }
  await quoteGeneration.fetchChubbQuote(buildLegacyChubbPayload(sources.quoteData), req.formaPago, req.paquete);
}

export function useRecotizar(deps: RecotizarDeps) {
  const isRecotizando = ref(false);
  const resultsLoading = computed(() => isRecotizando.value);

  const handleRecotizar = async (): Promise<void> => {
    if (isRecotizando.value) return;
    const idCotizacion = resolveIdCotizacion(deps.sources);
    if (!idCotizacion) return;

    isRecotizando.value = true;
    try {
      const req = {
        idCotizacion,
        formaPago: mapFormaPago(deps.plan.selectedPaymentMethod.value),
        paquete: mapPaquete(deps.plan.selectedPlanType.value),
      };
      await Promise.all([requoteQualitas(deps, req), requoteAna(deps, req), requoteChubb(deps, req)]);
      maintainExpandMode();
    } catch (error) {
      console.error('❌ Error al recotizar:', error);
    } finally {
      isRecotizando.value = false;
      maintainExpandMode();
    }
  };

  const recotizarAfterTick = async (): Promise<void> => {
    await nextTick();
    await handleRecotizar();
    maintainExpandMode();
  };

  const handlePaymentMethodChange = recotizarAfterTick;

  const handlePlanTypeSegmentChange = async (planType: string | number | boolean): Promise<void> => {
    if (typeof planType !== 'string') return;
    deps.plan.selectedPlanType.value = planType;
    await recotizarAfterTick();
  };

  return { resultsLoading, handlePaymentMethodChange, handlePlanTypeSegmentChange };
}
